import { useTranslation } from 'react-i18next'

const W = 640
const H = 420
const CX = W / 2
const CY = H / 2
const DOMAIN_ORDER = ['blood', 'supplements', 'nutrition', 'wellness', 'training']

/** Domains on an inner ring, their variables and findings fanned out behind them. */
function layoutNodes(nodes) {
  const pos = {}
  const domains = DOMAIN_ORDER.filter((d) => nodes.some((n) => n.type === 'domain' && n.domain === d))
  domains.forEach((d, i) => {
    const a = (2 * Math.PI * i) / domains.length - Math.PI / 2
    pos[`domain:${d}`] = { x: CX + 110 * Math.cos(a), y: CY + 110 * Math.sin(a), angle: a }
  })
  domains.forEach((d) => {
    const children = nodes.filter((n) => n.type !== 'domain' && n.domain === d)
    const base = pos[`domain:${d}`]
    children.forEach((n, j) => {
      const spread = 0.28
      const a = base.angle + (j - (children.length - 1) / 2) * spread
      pos[n.id] = { x: CX + 185 * Math.cos(a), y: CY + 170 * Math.sin(a) }
    })
  })
  return pos
}

function nodeLabel(node, t) {
  if (node.type === 'domain') return t(`systemView.domains.${node.domain}`)
  if (node.type === 'variable') return t(`systemView.variables.${node.variable}`)
  return t(`systemView.findings.${node.finding}`)
}

/** VH-222: SVG relation view. Null findings and missing data are drawn muted. */
export default function RelationView({ view }) {
  const { t } = useTranslation()
  if (!view?.nodes?.length) return null
  const pos = layoutNodes(view.nodes)
  const edges = view.edges.filter((e) => pos[e.source] && pos[e.target])
  return (
    <section className="space-y-2" data-testid="relation-view">
      <h3 className="text-base font-semibold">{t('systemView.relation.title')}</h3>
      <div className="overflow-x-auto border rounded-lg bg-surface-container">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[32rem] h-auto" role="img" aria-label={t('systemView.relation.aria')}>
          {edges.map((e) => {
            const a = pos[e.source]
            const b = pos[e.target]
            const cls = e.type === 'member' ? 'stroke-gray-300'
              : e.muted ? 'stroke-gray-300' : e.type === 'artifact' ? 'stroke-amber-500' : 'stroke-primary'
            return (
              <line key={e.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y} data-testid={`edge-${e.id}`} data-muted={String(!!e.muted)}
                className={cls} strokeWidth={e.type === 'member' ? 1 : e.muted ? 1 : 2}
                strokeDasharray={e.muted && e.type !== 'member' ? '4 4' : undefined}>
                {e.type === 'coupling' && (
                  <title>{`${t(`systemView.couplings.codes.${e.code}`)} — ${e.null_finding ? t('systemView.couplings.nullFinding') : t(`systemView.strength.${e.strength}`)}`}</title>
                )}
              </line>
            )
          })}
          {view.nodes.filter((n) => pos[n.id]).map((n) => {
            const p = pos[n.id]
            const domain = n.type === 'domain'
            return (
              <g key={n.id} data-testid={`node-${n.id}`}>
                <circle cx={p.x} cy={p.y} r={domain ? 22 : 5}
                  className={domain ? 'fill-primary' : n.type === 'training_finding' ? 'fill-amber-500' : 'fill-gray-500'} />
                <text x={p.x} y={domain ? p.y + 36 : p.y - 9} textAnchor="middle"
                  className={`fill-current ${domain ? 'text-xs font-semibold' : 'text-[10px]'}`}>
                  {nodeLabel(n, t)}
                </text>
              </g>
            )
          })}
        </svg>
      </div>
      <ul className="flex flex-wrap gap-3 text-xs text-gray-600">
        <li>{t('systemView.relation.legendCoupling')}</li>
        <li>{t('systemView.relation.legendMuted')}</li>
        <li>{t('systemView.relation.legendArtifact')}</li>
      </ul>
    </section>
  )
}
